import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { PaymentCell } from "../../components/payments/PaymentInfo";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { MapPin, Truck, WhatsappLogo, Robot, Clock } from "@phosphor-icons/react";

const STATE_STYLE = {
  NEW: "bg-[#F9E4B7] text-[#7A5A11]",
  QUOTED: "bg-[#D4E0D1] text-[#1A3317]",
  APPROVED: "bg-[#C8E6C9] text-[#1B5E20]",
  SCHEDULED: "bg-[#BBDEFB] text-[#0D47A1]",
  IN_PROGRESS: "bg-[#F9E4B7] text-[#7A5A11]",
};
const PRIORITY_STYLE = {
  LOW: "bg-[#E5E2D9] text-[#5C5A55]",
  NORMAL: "bg-[#D4E0D1] text-[#1A3317]",
  HIGH: "bg-[#F9E4B7] text-[#7A5A11]",
  URGENT: "bg-[#F4C7B9] text-[#7A2E18]",
};

function today() { return new Date().toISOString().slice(0, 10); }

export default function DailyServiceRequests() {
  const [date, setDate] = useState(today());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/daily-service-requests?date=${date}`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const autoGen = async () => {
    if (!window.confirm(`Auto-generate service requests for ${date} from customer schedules?`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/service-requests-auto-generate?date=${date}`);
      toast.success(`Created ${r.data.created} · Skipped ${r.data.skipped}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const sendWA = async (rid) => {
    try {
      const r = await api.post(`/service-requests/${rid}/whatsapp`, {});
      window.open(r.data.url, "_blank");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const zones = data?.zones || [];

  return (
    <AppShell title="Daily Collection List" subtitle={`Grouped by zone · ${data?.total || 0} pickups`}
      actions={
        <div className="flex gap-2">
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
                 data-testid="daily-date"
                 className="h-10 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          <button onClick={autoGen} disabled={busy} data-testid="btn-auto-gen"
                  className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white rounded-md text-sm font-medium">
            <Robot size={14} weight="bold"/>{busy ? "…" : "Auto-generate"}
          </button>
        </div>
      }>
      {zones.length === 0 && (
        <div className="bg-white border border-[#E5E2D9] rounded-md p-12 text-center text-[#8A8780]" data-testid="daily-empty">
          <Clock size={36} color="#8A8780" weight="duotone" className="mx-auto mb-3"/>
          No pickups scheduled for {date}. Use Auto-generate to create today's list from customer plans.
        </div>
      )}
      <div className="space-y-5" data-testid="daily-zones">
        {zones.map(z => (
          <div key={z.zone_id || "none"} className="bg-white border border-[#E5E2D9] rounded-md">
            <div className="px-5 py-3 border-b border-[#E5E2D9] flex items-center justify-between">
              <div className="font-heading text-base flex items-center gap-2">
                <MapPin size={16} color="#2D5A27" weight="duotone"/>{z.zone_name}
              </div>
              <div className="text-xs text-[#5C5A55]">{z.count} pickup{z.count !== 1 ? "s" : ""}</div>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-[#8A8780] bg-[#FAFAF7]">
                  <th className="px-5 py-2">Customer</th>
                  <th className="px-5 py-2">Slot</th>
                  <th className="px-5 py-2">Category</th>
                  <th className="px-5 py-2">Priority</th>
                  <th className="px-5 py-2">Staff / Vehicle</th>
                  <th className="px-5 py-2">Status</th>
                  <th className="px-5 py-2">Payment</th>
                  <th className="px-5 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {z.rows.map(r => (
                  <tr key={r.id} className="border-t border-[#E5E2D9]">
                    <td className="px-5 py-2">
                      <div className="font-medium">{r.customer_name}</div>
                      <div className="text-[10px] font-mono text-[#8A8780]">{r.customer_code} · {r.request_no}</div>
                    </td>
                    <td className="px-5 py-2 font-mono text-xs">{r.scheduled_slot || r.preferred_slot || "—"}</td>
                    <td className="px-5 py-2 text-xs">{r.service_category}</td>
                    <td className="px-5 py-2">
                      <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${PRIORITY_STYLE[r.priority]}`}>{r.priority}</span>
                    </td>
                    <td className="px-5 py-2 text-xs">
                      {r.assigned_staff_name || <span className="text-[#8A8780]">—</span>}
                      {r.assigned_vehicle_no && <div className="text-[10px] text-[#5C5A55] flex items-center gap-1 mt-0.5"><Truck size={9}/>{r.assigned_vehicle_no}</div>}
                    </td>
                    <td className="px-5 py-2">
                      <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${STATE_STYLE[r.status] || "bg-[#E5E2D9]"}`}>{r.status}</span>
                    </td>
                    <td className="px-5 py-2"><PaymentCell payment={r.payment} testid={`daily-payment-${r.id}`}/></td>
                    <td className="px-5 py-2 text-right space-x-2">
                      {r.customer_phone && (
                        <button onClick={() => sendWA(r.id)} data-testid={`wa-${r.id}`}
                                className="text-[#25D366] text-xs inline-flex items-center gap-1 hover:underline">
                          <WhatsappLogo size={12} weight="fill"/>WA
                        </button>
                      )}
                      <Link to={`/operations/service-requests/${r.id}`} className="text-[#2D5A27] text-xs hover:underline">open</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </AppShell>
  );
}
